<script setup>
import { ref, computed, watch, toRef } from 'vue'
import { useTemplateFields } from '@/composables/useTemplateFields'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

const props = defineProps({
  fields: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue'])

const fieldsRef = toRef(props, 'fields')
const { sortedFields, isFieldVisible } = useTemplateFields(fieldsRef)

const formValues = ref({})

const getDefaultValue = (type) => {
  switch (type) {
    case 0: return ''
    case 1: return ''
    case 2: return false
    case 3: return ''
    case 4: return undefined
    default: return ''
  }
}

const initFormValues = () => {
  const values = {}
  const modelByKey = {}
  for (const entry of props.modelValue) {
    modelByKey[entry.key] = entry.value
  }
  for (const field of sortedFields.value) {
    values[field.id] = field.key in modelByKey ? modelByKey[field.key] : getDefaultValue(field.type)
  }
  formValues.value = values
}

watch(sortedFields, () => {
  initFormValues()
}, { immediate: true })

const updateField = (fieldId, value) => {
  formValues.value = { ...formValues.value, [fieldId]: value }
}

// Computed visibility map — guaranteed reactive
const visibilityMap = computed(() => {
  const vals = formValues.value
  const map = {}
  for (const field of sortedFields.value) {
    map[field.id] = isFieldVisible(field.id, vals)
  }
  return map
})

const emitData = () => {
  const vals = formValues.value
  const data = sortedFields.value
    .filter(field => visibilityMap.value[field.id])
    .filter(field => vals[field.id] !== undefined && vals[field.id] !== '')
    .map(field => ({
      key: field.key,
      value: field.type === 1 && vals[field.id] !== '' ? Number(vals[field.id]) : vals[field.id],
      type: field.type,
    }))
  emit('update:modelValue', data)
}

watch(formValues, () => {
  emitData()
}, { deep: true })

const getCollectedData = () => {
  const vals = formValues.value
  return sortedFields.value
    .filter(field => visibilityMap.value[field.id])
    .map(field => ({
      key: field.key,
      value: vals[field.id] !== undefined && vals[field.id] !== '' ? (field.type === 1 ? Number(vals[field.id]) : vals[field.id]) : null,
      type: field.type,
    }))
}

defineExpose({ getCollectedData })
</script>

<template>
  <div class="space-y-3">
    <div
      v-for="field in sortedFields"
      :key="field.id"
      v-show="visibilityMap[field.id]"
      :class="{ 'ml-4': field.parentId }"
    >
      <div v-if="field.type === 0" class="space-y-1">
        <label class="text-sm font-medium">{{ field.key }}</label>
        <Input
          :model-value="formValues[field.id]"
          @update:model-value="(val) => updateField(field.id, val)"
          type="text"
        />
      </div>

      <div v-else-if="field.type === 1" class="space-y-1">
        <label class="text-sm font-medium">{{ field.key }}</label>
        <Input
          :model-value="formValues[field.id]"
          @update:model-value="(val) => updateField(field.id, val)"
          type="number"
        />
      </div>

      <div v-else-if="field.type === 2" class="flex items-center">
        <Switch
          :model-value="formValues[field.id] || false"
          @update:model-value="(val) => updateField(field.id, !!val)"
        />
        <Label class="ml-2">{{ field.key }}</Label>
      </div>

      <div v-else-if="field.type === 3" class="space-y-1">
        <label class="text-sm font-medium">{{ field.key }}</label>
        <Textarea
          :model-value="formValues[field.id]"
          @update:model-value="(val) => updateField(field.id, val)"
        />
      </div>

      <div v-else-if="field.type === 4" class="space-y-1">
        <label class="text-sm font-medium">{{ field.key }}</label>
        <RadioGroup
          :model-value="formValues[field.id]"
          @update:model-value="(val) => updateField(field.id, val)"
        >
          <div v-for="option in field.options" :key="option" class="flex items-center space-y-0 gap-x-3">
            <RadioGroupItem :value="option" />
            <Label class="font-normal">{{ option }}</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  </div>
</template>
